import React, { useEffect, useState } from 'react';
import { CreditCard, LogOut } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useBillingReturn } from '../../hooks/useBillingReturn';
import { SubscriptionService } from '../../services/billing/subscription.service';
import type { PlanDto } from '../../dtos/billing/subscription.dto';
import { ACCOUNT_STATE_CHANGED_EVENT } from '../../constants/navigationEvents';
import { describeApiError } from '../../utils/apiErrors';
import { BILLING_DISABLED_TITLE, formatAccountDate, isAccountClosed, planAction } from '../../utils/subscription';
import PlanCards from './PlanCards';
import AuditLogExportSection from '../admin/AuditLogExportSection';
import DataExportSection from '../admin/export/DataExportSection';
import SignedBaaSection from '../admin/SignedBaaSection';
import peloraLogo from '../../assets/images/logofullblack.png';

interface AccountSuspendedPageProps {
  onLogout: () => void | Promise<void>;
}

/**
 * Export-only page for suspended and canceled accounts (Phase 13, Q9 / ACC-14). Explains the
 * state, keeps the exports that stay open (audit log, signed BAA), lets managers pay to restore
 * access through Stripe Checkout (or, with a declined card on file, "Replace card": Checkout in
 * setup mode, which pays the open invoice), and shows a plain "closed" state once the 90-day
 * export window has ended (402 ACCOUNT_CLOSED).
 */
const AccountSuspendedPage: React.FC<AccountSuspendedPageProps> = ({ onLogout }) => {
  const { user } = useAuth();
  const canManage = useCanPerform('manage:subscription');
  const subscription = user?.subscription ?? null;
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [plansError, setPlansError] = useState('');
  const [busyPlan, setBusyPlan] = useState<PlanDto['id'] | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [closedByServer, setClosedByServer] = useState(false);
  // Back from Checkout: re-read permissions; once active, the route sends the user to the dashboard
  useBillingReturn();

  const closed = closedByServer || isAccountClosed(subscription);

  useEffect(() => {
    const onChange = (event: Event) => {
      if ((event as CustomEvent<{ code?: string } | undefined>).detail?.code === 'ACCOUNT_CLOSED') setClosedByServer(true);
    };
    window.addEventListener(ACCOUNT_STATE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ACCOUNT_STATE_CHANGED_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!canManage || closed) return undefined;
    let cancelled = false;
    SubscriptionService.getPlans()
      .then((catalog) => { if (!cancelled) setPlans(catalog.plans); })
      .catch((err: unknown) => { if (!cancelled) setPlansError(describeApiError(err, 'Unable to load plans.')); });
    return () => { cancelled = true; };
  }, [canManage, closed]);

  const choosePlan = async (plan: PlanDto) => {
    setBusyPlan(plan.id);
    setCheckoutError('');
    try {
      SubscriptionService.redirectTo(await SubscriptionService.checkout(plan.id));
    } catch (err) {
      setCheckoutError(describeApiError(err, 'Unable to start checkout.'));
      setBusyPlan(null);
    }
  };

  const replaceCard = async () => {
    setCardBusy(true);
    setCheckoutError('');
    try {
      SubscriptionService.redirectTo(await SubscriptionService.addPaymentMethod());
    } catch (err) {
      setCheckoutError(describeApiError(err, 'Unable to open the card page.'));
      setCardBusy(false);
    }
  };

  const timeZone = user?.agencyTimezone;
  const exportUntil = formatAccountDate(subscription?.exportUntil, timeZone);
  const billingEnabled = subscription?.billingEnabled === true;
  const canceled = subscription?.status === 'canceled';
  const hasCard = subscription?.hasPaymentMethod === true;
  // Suspended with a declined card: the subscription still exists, so replace the card (no new Checkout)
  const fixCard = !!subscription && planAction(subscription.status, hasCard, subscription.cancelAtPeriodEnd) === 'none';

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-800" data-testid="account-suspended-page">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-center justify-between">
          <img src={peloraLogo} alt="Pelora Health" className="h-auto w-40 object-contain" />
          <button type="button" onClick={() => void onLogout()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
            <LogOut size={16} aria-hidden="true" /> Sign out
          </button>
        </div>

        {closed ? (
          <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="account-closed">
            <h1 className="text-xl font-semibold text-slate-900">This account is closed</h1>
            <p className="mt-2 text-sm text-slate-600">The subscription was canceled and the data export window has ended. Contact support if you have questions.</p>
          </section>
        ) : (
          <>
            <section className="rounded-xl border border-slate-200 bg-white p-6">
              <h1 className="text-xl font-semibold text-slate-900">{canceled ? 'Your subscription has ended' : 'Your account is suspended'}</h1>
              <p className="mt-2 text-sm text-slate-600">
                {canceled
                  ? `Patient records can no longer be opened or changed. You can still export your data${exportUntil ? ` until ${exportUntil}` : ''}.`
                  : 'Patient records can no longer be opened or changed. Your data is kept, and you can still export it.'}
              </p>
            </section>

            <section aria-labelledby="restore-heading" className="rounded-xl border border-slate-200 bg-white p-6">
              <h2 id="restore-heading" className="text-base font-semibold text-slate-900">{fixCard ? 'Replace your card to restore access' : 'Choose a plan to restore access'}</h2>
              {canManage ? (
                <>
                  <p className="mt-1 text-sm text-slate-600">
                    {fixCard
                      ? 'Your last payment failed. Enter a new card on our payment provider\'s secure page; the open invoice is paid with it and access returns as soon as the payment goes through.'
                      : 'Payment is handled on a secure page from our payment provider. Access returns as soon as the payment goes through.'}
                  </p>
                  {!billingEnabled && <p className="mt-1 text-xs text-slate-500" title={BILLING_DISABLED_TITLE}>{BILLING_DISABLED_TITLE}.</p>}
                  {plansError && !fixCard && <p className="mt-3 text-sm text-red-700" role="alert">{plansError}</p>}
                  {checkoutError && <p className="mt-3 text-sm text-red-700" role="alert">{checkoutError}</p>}
                  <div className="mt-3">
                    {fixCard ? (
                      <button
                        type="button"
                        onClick={replaceCard}
                        disabled={cardBusy || !billingEnabled}
                        title={billingEnabled ? undefined : BILLING_DISABLED_TITLE}
                        className="inline-flex items-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CreditCard size={16} aria-hidden="true" /> {cardBusy ? 'Opening...' : 'Replace card'}
                      </button>
                    ) : (
                      <PlanCards
                        plans={plans}
                        currentPlan={subscription?.plan ?? null}
                        status={subscription?.status ?? null}
                        billingEnabled={billingEnabled}
                        hasPaymentMethod={hasCard}
                        canManage
                        busyPlan={busyPlan}
                        onChoose={choosePlan}
                      />
                    )}
                  </div>
                </>
              ) : (
                <p className="mt-1 text-sm text-slate-600">Ask an owner or admin of your organization to choose a plan.</p>
              )}
            </section>

            <section aria-labelledby="exports-heading" className="rounded-xl border border-slate-200 bg-white p-6">
              <h2 id="exports-heading" className="text-base font-semibold text-slate-900">Available exports</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                <li><a href="#audit-log-export" className="underline underline-offset-2">HIPAA audit log</a> (CSV, free while the account is suspended or canceled; owners and admins)</li>
                <li>Signed Business Associate Agreement (owners and admins)</li>
                <li>Claim PDFs already generated remain downloadable from the claims register.</li>
              </ul>
              <p className="mt-3 text-xs text-slate-500">A full agency data export will be added here.</p>
            </section>

            <SignedBaaSection />
            <DataExportSection />
            <AuditLogExportSection />
          </>
        )}
      </div>
    </main>
  );
};

export default AccountSuspendedPage;
